import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, IconButton, Tooltip, useTheme } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
  candidateDetailsSelectors,
  clearCandidateDetails,
  fetchCandidateDetails,
} from '@redux/candidateDetails';
import { appBarHeight, navRailBreakpoint } from '@theme/variables';
import { touchTargetSx } from 'theme/touchTarget';
import { drawerShadow } from 'theme/shadows';
import { candidateDetailsPath, isStatusLoading } from '@utils';
import { Loader } from 'components/common/Loader/Loader';
import {
  CandidateSection,
  ClosedBanner,
  getCandidateSections,
} from '../CandidateDetailSections/CandidateDetailSections';
import { CandidateDetailsActionButton } from '../CandidaterDetailsActionButton/CandidaterDetailsActionButton';
import { CandidateDetailsHeader } from '../CandidateDetailsHeader/CandidateDetailsHeader';

const DRAWER_WIDTH = '44rem';

interface ICandidateDrawerProps {
  candidateId: string | null;
  onClose: () => void;
}

const DrawerBody: React.FC = () => {
  const { apiStatus, data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  if (isStatusLoading(apiStatus) || !data) return <Loader />;

  return (
    <Box pb={12}>
      <Box px={3} pt={3} sx={{ '&:empty': { display: 'none' } }}>
        <ClosedBanner data={data} />
      </Box>
      {getCandidateSections(data).map((section) => (
        <CandidateSection key={section.title} title={section.title}>
          {section.content}
        </CandidateSection>
      ))}
    </Box>
  );
};

export const CandidateDrawer: React.FC<ICandidateDrawerProps> = ({
  candidateId,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const theme = useTheme();
  const { data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  useEffect(() => {
    if (!candidateId) return undefined;

    dispatch(fetchCandidateDetails(candidateId));

    return () => {
      dispatch(clearCandidateDetails());
    };
  }, [candidateId, dispatch]);

  const open = Boolean(candidateId);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  const header = (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1.5}
      px={3}
      py={2}
      borderBottom={`1px solid ${theme.palette.background.divider}`}
    >
      <CandidateDetailsHeader data={data} variant="drawer" />
      <Tooltip title="Open the full page" placement="top">
        <IconButton
          size="small"
          aria-label="Open the full candidate page"
          onClick={() =>
            candidateId && navigate(candidateDetailsPath(candidateId, search))
          }
          sx={touchTargetSx}
        >
          <OpenInFullIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        aria-label="Close"
        onClick={onClose}
        sx={touchTargetSx}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  const actions = data ? (
    <Box
      position="sticky"
      bottom={0}
      sx={{ backgroundColor: 'common.white', zIndex: 1 }}
    >
      <CandidateDetailsActionButton data={data} />
    </Box>
  ) : null;

  const body = (
    <>
      {header}
      <Box flexGrow={1} overflow="auto">
        {candidateId && <DrawerBody />}
      </Box>
      {actions}
    </>
  );

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          maxWidth: '100%',
          boxShadow: drawerShadow,
          display: 'flex',
          flexDirection: 'column',
          top: { xs: appBarHeight, [navRailBreakpoint]: 0 },
          height: {
            xs: `calc(100% - ${appBarHeight})`,
            [navRailBreakpoint]: '100%',
          },
        },
      }}
    >
      {body}
    </Drawer>
  );
};
